/**
 * Judgment Taxonomy
 * =================
 *
 * Great judgment is not a single, one-size-fits-all quantity. Being right about
 * *whether a bet will pay off* is a different skill from *estimating how long it
 * will take*, from *pricing a risk*, from *spotting an opportunity nobody else
 * sees*. A person can be a superb risk assessor and a chronically optimistic
 * estimator at the same time. The existing Decision Intelligence system
 * (migration 463 — `decision_signals` / `outcome_observations` /
 * `judgment_profiles`) captures a single calibration score per person × domain.
 * This module adds the missing dimension: a *type* of judgment, a *lens* through
 * which a person tends to see, and the set of *scoring dimensions* that describe
 * the distinct ways a person can be right or wrong.
 *
 * This file is the pure, dependency-free registry — the "rich model of how to
 * think about judgment." It is deliberately data-only (like `entitlements.ts`)
 * so it can run on both client and server and be unit-tested in isolation. The
 * scoring math lives in `./judgmentScoring`; lens inference in `./judgmentLens`.
 *
 * Design spec: docs/9-exploration/judgment-analytics/DESIGN.md
 */

import type { JudgmentDomain } from '../../types/decisionIntelligence';

// ============================================================================
// Judgment axes — what we capture when a person makes a call
// ============================================================================

/**
 * Three separable things are true whenever a person makes a judgment, and the
 * product should capture all three rather than collapsing them into one number:
 *
 *  - **Belief** — *what* you think: your position or the option you favour. For a
 *    probabilistic call this is the direction of your probability; for a choice
 *    it is which option you back.
 *  - **Confidence** — *how sure* you are of that belief. Two people can both back
 *    the same option, one at 0.6 and one at 0.95.
 *  - **Stakes** — *how much it matters to you* that this goes your way. This is
 *    the intensity of the preference, and it is what lets intensity-weighted
 *    resolution work: if I want Chick-fil-A at a 4 and my wife wants Whataburger
 *    at a 9, all else equal we go to Whataburger. Stakes are not confidence — you
 *    can be highly confident about something you barely care about, and vice
 *    versa.
 *
 * Belief and confidence together are what a proper scoring rule grades (did the
 * probability match reality). Stakes are *not* graded for accuracy the same way;
 * they weight aggregation and are profiled separately (does a person reserve high
 * stakes for what matters, or inflate everything?).
 *
 * All three are normalised to [0,1] here; capture UIs typically collect 1–10.
 */
export interface JudgmentAxes {
  /** Position/direction. For a binary call, the probability of the positive outcome. [0,1] */
  belief: number;
  /** How sure the person is of their belief. [0,1] */
  confidence: number;
  /** How much the outcome matters to this person — the intensity of preference. [0,1] */
  stakes: number;
}

/** Normalise a 1–10 capture-scale value to [0,1]. */
export function normalizeScale10(value: number): number {
  return Math.min(1, Math.max(0, (value - 1) / 9));
}

// ============================================================================
// Judgment type — the *kind* of call being made
// ============================================================================

/**
 * The kind of cognitive task a judgment performs. Orthogonal to `JudgmentDomain`
 * (the subject area). A person has a separate track record for each
 * `JudgmentType × JudgmentDomain` cell — the grid a leader reads to decide whose
 * judgment to seek for which kind of call.
 */
export type JudgmentType =
  | 'outcome_prediction' // Will this succeed / ship / hit its target?
  | 'effort_estimate' //    How long / how much will this take?
  | 'risk_assessment' //    What could go wrong, how likely and severe?
  | 'opportunity_spotting' // Is there upside here that others are missing?
  | 'people_judgment' //    Will this hire / person / team work out?
  | 'value_ranking' //      Is X more important / more valuable than Y?
  | 'timing'; //            Is now the right moment to act (or wait)?

/**
 * The shape of the "correct answer" a judgment type resolves against. This
 * governs which scoring functions apply: a binary prediction is scored with
 * Brier / log loss; a magnitude estimate with relative error; an ordinal ranking
 * with rank correlation.
 */
export type OutcomeScale =
  | 'binary' //      resolved true/false (succeeded vs not)
  | 'probability' // a stated probability scored against a binary outcome
  | 'magnitude' //   a numeric estimate scored against an actual value
  | 'ordinal' //     a ranking scored against the realized order
  | 'categorical'; // one of several discrete outcomes

export interface JudgmentTypeDef {
  key: JudgmentType;
  label: string;
  /** One line a product surface can show next to the type. */
  description: string;
  /** How the correct answer is shaped — selects the scoring family. */
  outcomeScale: OutcomeScale;
  /**
   * How a call of this type is *resolved* — the outcome evidence that closes it.
   * Plain language, surfaced in the capture/verdict UI so people know what will
   * later be used to score them.
   */
  resolvesAgainst: string;
  /** The scoring dimensions that meaningfully apply to this type. */
  dimensions: ScoringDimension[];
  /**
   * Whether a consensus/crowd reference is available for this type, enabling the
   * non-consensus edge. True when many actors routinely signal on the same call.
   */
  supportsConsensusEdge: boolean;
}

// ============================================================================
// Scoring dimensions — the distinct ways of being right
// ============================================================================

/**
 * The axes along which a judgment track record is measured. Crucially, these are
 * *not* interchangeable — calibration and resolution can move in opposite
 * directions, and a person can score high on one while low on another.
 */
export type ScoringDimension =
  | 'calibration' //      Do your 70%s happen ~70% of the time? (reliability)
  | 'resolution' //       Do you actually separate hits from misses? (discrimination)
  | 'accuracy' //         Raw hit rate — how often the call was right.
  | 'consensus_edge' //   When you went against the crowd, were you right? (per-call)
  | 'insight' //          Aligned most of the time, selectively & correctly divergent (the sweet spot)
  | 'distinctiveness' //  Raw differentness from the group — an input, NOT a target on its own.
  | 'timeliness' //       For estimates: did the outcome land when predicted?
  | 'coverage'; //        Volume/breadth of resolved calls — the confidence floor.

export interface ScoringDimensionDef {
  key: ScoringDimension;
  label: string;
  description: string;
  /** true → higher raw value is better; false → lower is better. */
  higherIsBetter: boolean;
}

// ============================================================================
// Judgment lens — the cognitive stance a person tends to take
// ============================================================================

/**
 * A person's characteristic way of *seeing* a decision, inferred from the
 * pattern of their signals (not self-reported). Two people who are equally
 * accurate can be valuable for opposite reasons: one reliably surfaces what
 * breaks, the other reliably surfaces what's possible. A leader assembling a
 * decision needs both — and needs to know which is which.
 */
export type JudgmentLens =
  | 'risk_mitigation' //   Downside-first; surfaces what could break.
  | 'possibility' //       Upside-first; surfaces what could be won.
  | 'systems' //           Second-order; surfaces interactions and knock-ons.
  | 'consensus_anchored' //Moves with the group; a reliable read of the room.
  | 'independent' //       Forms a view apart from the group; a source of dissent.
  | 'empirical'; //        Anchors to evidence and base rates over intuition.

export interface JudgmentLensDef {
  key: JudgmentLens;
  label: string;
  description: string;
  /** The complementary lens a leader should pair this one with, if any. */
  complement?: JudgmentLens;
}

// ============================================================================
// Registries
// ============================================================================

export const SCORING_DIMENSIONS: Record<ScoringDimension, ScoringDimensionDef> = {
  calibration: {
    key: 'calibration',
    label: 'Calibration',
    description:
      'Reliability of stated confidence — whether events a person calls 70% likely actually occur about 70% of the time.',
    higherIsBetter: true,
  },
  resolution: {
    key: 'resolution',
    label: 'Resolution',
    description:
      'Discrimination — whether a person actually separates the things that happen from the things that do not, rather than hedging toward the base rate.',
    higherIsBetter: true,
  },
  accuracy: {
    key: 'accuracy',
    label: 'Accuracy',
    description: 'Raw hit rate — the share of resolved calls that turned out to be right.',
    higherIsBetter: true,
  },
  consensus_edge: {
    key: 'consensus_edge',
    label: 'Non-consensus edge',
    description:
      'Per-call information added beyond the crowd — how much better than the consensus a person was, weighted so that being right *against* a confident crowd counts far more than being right *with* it.',
    higherIsBetter: true,
  },
  insight: {
    key: 'insight',
    label: 'Unique insight',
    description:
      'The sweet spot: a person who is aligned with consensus most of the time (a good teammate) but is selectively and *correctly* divergent in specific pockets. This — not raw contrarianism — is the employable edge.',
    higherIsBetter: true,
  },
  distinctiveness: {
    key: 'distinctiveness',
    label: 'Distinctiveness',
    description:
      'Raw differentness from the group centroid. An input signal, NOT a target on its own — being different everywhere is noise. Only valuable when it is localized and paired with accuracy (see Unique insight).',
    higherIsBetter: false,
  },
  timeliness: {
    key: 'timeliness',
    label: 'Timeliness',
    description:
      'For estimates — how close the realized timeline/magnitude was to what the person predicted.',
    higherIsBetter: true,
  },
  coverage: {
    key: 'coverage',
    label: 'Coverage',
    description:
      'Volume and breadth of resolved calls. Not a skill measure — the confidence floor that determines how much weight any other score deserves.',
    higherIsBetter: true,
  },
};

export const JUDGMENT_LENSES: Record<JudgmentLens, JudgmentLensDef> = {
  risk_mitigation: {
    key: 'risk_mitigation',
    label: 'Risk mitigation',
    description:
      'Sees a decision downside-first — dense risk flags, more pause/stop calls, quickest to name what could break.',
    complement: 'possibility',
  },
  possibility: {
    key: 'possibility',
    label: 'Possibility',
    description:
      'Sees a decision upside-first — quickest to name what could be won, more proceed calls on ambiguous bets.',
    complement: 'risk_mitigation',
  },
  systems: {
    key: 'systems',
    label: 'Systems',
    description:
      'Sees second-order effects — interactions, dependencies, and knock-on consequences others miss.',
  },
  consensus_anchored: {
    key: 'consensus_anchored',
    label: 'Consensus-anchored',
    description:
      'Judgments track the group closely — a reliable read of where the room is, less a source of new information.',
    complement: 'independent',
  },
  independent: {
    key: 'independent',
    label: 'Independent',
    description:
      'Forms a view apart from the group — a source of genuine dissent whose value depends entirely on accuracy.',
    complement: 'consensus_anchored',
  },
  empirical: {
    key: 'empirical',
    label: 'Empirical',
    description:
      'Anchors judgments to evidence and base rates rather than intuition — well-calibrated, rarely wildly wrong.',
  },
};

const D = SCORING_DIMENSIONS; // brevity in the type table below

export const JUDGMENT_TYPES: Record<JudgmentType, JudgmentTypeDef> = {
  outcome_prediction: {
    key: 'outcome_prediction',
    label: 'Outcome prediction',
    description: 'Will this succeed, ship, or hit its target?',
    outcomeScale: 'probability',
    resolvesAgainst:
      'the recorded outcome observation (succeeded / shipped vs failed / cancelled).',
    dimensions: [
      D.calibration.key,
      D.resolution.key,
      D.accuracy.key,
      D.consensus_edge.key,
      D.coverage.key,
    ],
    supportsConsensusEdge: true,
  },
  effort_estimate: {
    key: 'effort_estimate',
    label: 'Effort / estimate',
    description: 'How long will this take, or how much will it cost?',
    outcomeScale: 'magnitude',
    resolvesAgainst: 'the actual realized timeline or cost at delivery.',
    dimensions: [D.timeliness.key, D.accuracy.key, D.calibration.key, D.coverage.key],
    supportsConsensusEdge: true,
  },
  risk_assessment: {
    key: 'risk_assessment',
    label: 'Risk assessment',
    description: 'What could go wrong here, and how likely and severe is it?',
    outcomeScale: 'probability',
    resolvesAgainst: 'whether the flagged risks actually materialized, and how badly.',
    dimensions: [
      D.calibration.key,
      D.resolution.key,
      D.consensus_edge.key,
      D.insight.key,
      D.coverage.key,
    ],
    supportsConsensusEdge: true,
  },
  opportunity_spotting: {
    key: 'opportunity_spotting',
    label: 'Opportunity spotting',
    description: 'Is there upside here that others are missing?',
    outcomeScale: 'binary',
    resolvesAgainst: 'whether the identified upside was realized when pursued.',
    // The non-consensus heartland — but the target is the *insight* sweet spot
    // (aligned teammate, correct where they diverge), not raw contrarianism, so
    // `insight` leads, backed by per-call `consensus_edge`.
    dimensions: [
      D.insight.key,
      D.consensus_edge.key,
      D.accuracy.key,
      D.resolution.key,
      D.coverage.key,
    ],
    supportsConsensusEdge: true,
  },
  people_judgment: {
    key: 'people_judgment',
    label: 'People judgment',
    description: 'Will this hire, person, or team work out?',
    outcomeScale: 'binary',
    resolvesAgainst: 'the person/team outcome at a later review point.',
    dimensions: [D.accuracy.key, D.calibration.key, D.consensus_edge.key, D.coverage.key],
    supportsConsensusEdge: true,
  },
  value_ranking: {
    key: 'value_ranking',
    label: 'Value ranking',
    description: 'Is X more important or valuable than Y?',
    outcomeScale: 'ordinal',
    resolvesAgainst: 'the realized relative value/impact of the ranked items.',
    dimensions: [D.accuracy.key, D.distinctiveness.key, D.coverage.key],
    supportsConsensusEdge: false,
  },
  timing: {
    key: 'timing',
    label: 'Timing',
    description: 'Is now the right moment to act, or to wait?',
    outcomeScale: 'binary',
    resolvesAgainst: 'whether acting (or waiting) at that moment proved correct in hindsight.',
    dimensions: [D.accuracy.key, D.consensus_edge.key, D.calibration.key, D.coverage.key],
    supportsConsensusEdge: true,
  },
};

// ============================================================================
// Ordered lists & helpers
// ============================================================================

export const ALL_JUDGMENT_TYPES: JudgmentType[] = Object.keys(JUDGMENT_TYPES) as JudgmentType[];
export const ALL_JUDGMENT_LENSES: JudgmentLens[] = Object.keys(JUDGMENT_LENSES) as JudgmentLens[];
export const ALL_SCORING_DIMENSIONS: ScoringDimension[] = Object.keys(
  SCORING_DIMENSIONS,
) as ScoringDimension[];

/**
 * The `JudgmentType × JudgmentDomain` cell a person's track record is keyed by.
 * This is the addressable unit a leader queries: "whose *risk* judgment on
 * *gtm* should I weight?"
 */
export interface JudgmentCell {
  type: JudgmentType;
  domain: JudgmentDomain;
}

export function judgmentCellKey(cell: JudgmentCell): string {
  return `${cell.type}::${cell.domain}`;
}

export function isJudgmentType(value: string): value is JudgmentType {
  return Object.prototype.hasOwnProperty.call(JUDGMENT_TYPES, value);
}

export function isJudgmentLens(value: string): value is JudgmentLens {
  return Object.prototype.hasOwnProperty.call(JUDGMENT_LENSES, value);
}

/** The scoring dimensions that apply to a given judgment type. */
export function dimensionsForType(type: JudgmentType): ScoringDimension[] {
  return JUDGMENT_TYPES[type].dimensions;
}

/**
 * The single dimension that best expresses "good judgment" for this type — the
 * one a leader-facing ranking should sort by when reading that type's grid.
 * Prediction-shaped types lead with the non-consensus edge (being right against
 * the crowd); estimate types lead with timeliness; ranking with accuracy.
 */
export function leadDimensionForType(type: JudgmentType): ScoringDimension {
  return JUDGMENT_TYPES[type].dimensions[0];
}
